import React from 'react';
import DOMPurify from 'dompurify';
import { componentTypes, Snippet } from '@white-nl/instantsearch-components';

import { Hit as CatalogHit } from '../../../catalog/results/Hit';
import { Hit as DownloadsHit } from '../../../downloads/results/Hit';
import Picture from '../../../ui/Picture';
import { ArrowRightSharpRegular } from '../../../ui/icons';

export function OtherHit({ hit }: componentTypes.HitProps) {
  const {
    label,
    title,
    image,
    url,
  } = hit;

  return (
    <div className="col-12">
      <a href={url} className="result-other">
        <div className="content">
          {label && (<span className="badge bg-danger mb-2">{label}</span>)}
          {/* eslint-disable-next-line react/no-danger */}
          <h3 className="h4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
          <Snippet
            hit={hit}
            attribute="content"
            classNames={{ root: 'text' }}
          />
        </div>
        {image && (
          <div className="image">
            <Picture image={image} defaultTransform="xs-min" loading="lazy" />
          </div>
        )}
      </a>
    </div>
  );
}

export function ApplicationServiceHit({ hit }: componentTypes.HitProps) {
  const {
    title,
    url,
    image,
  } = hit;

  return (
    <div className="col-12 col-sm-6 col-lg-4">
      <a href={url} className="card-services">
        <div className="card-services-img">
          <Picture image={image} defaultTransform="lg" loading="lazy" />
        </div>
        <div className="card-services-body">
          {/* eslint-disable-next-line react/no-danger */}
          <h3 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
        </div>
      </a>
    </div>
  );
}

export function FaqHit({ hit }: componentTypes.HitProps) {
  const { title, url } = hit;

  return (
    <div className="col-12">
      <a href={url} className="result-other">
        <div className="content">
          {/* eslint-disable-next-line react/no-danger */}
          <h3 className="h4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
          <Snippet
            hit={hit}
            attribute="content"
            classNames={{ root: 'text' }}
          />
        </div>
      </a>
    </div>
  );
}

export default function Overview({ results, additionalData }: componentTypes.StatsProps) {
  const { hits } = results;

  const groupedHits = hits.reduce((group: Record<string, typeof hits>, item) => {
    const grouped = group;
    // @ts-ignore
    if (!grouped[item.type]) {
      // @ts-ignore
      grouped[item.type] = [];
    }
    // @ts-ignore
    grouped[item.type].push(item);
    return grouped;
  }, {}) || [];

  return (
    <>
      {Object.entries(groupedHits).sort().map(([key, items]) => (
        <div className="row align-items-end mb-8" key={key}>
          <div className="col-12 col-lg-auto">
            <h2 className="h3 mb-0">
              {Craft.t('search', key.slice(2))}
            </h2>
          </div>
          <div className="col-12 order-lg-1 mt-2 mt-lg-5">
            <div className="row gy-3">
              {(key.slice(2) === 'Other') ? (
                items.slice(0, 2).map((hit) => (
                  <OtherHit
                    results={results}
                    hit={hit}
                    additionalData={[]}
                    key={hit.objectID}
                  />
                ))
              ) : (
                items.slice(0, 3).map((hit) => (
                  (key.slice(2) === 'Products' && (
                    <CatalogHit
                      results={results}
                      hit={hit}
                      additionalData={[]}
                      key={hit.objectID}
                    />
                  ))
                  || (key.slice(2) === 'Applications' && (
                    <ApplicationServiceHit
                      results={results}
                      hit={hit}
                      additionalData={[]}
                      key={hit.objectID}
                    />
                  ))
                  || (key.slice(2) === 'Services' && (
                    <ApplicationServiceHit
                      results={results}
                      hit={hit}
                      additionalData={[]}
                      key={hit.objectID}
                    />
                  ))
                  || (key.slice(2) === 'Downloads' && (
                    <DownloadsHit
                      results={results}
                      hit={hit}
                      additionalData={additionalData}
                      key={hit.objectID}
                    />
                  ))
                  || (key.slice(2) === 'FAQ' && (
                    <FaqHit
                      results={results}
                      hit={hit}
                      additionalData={[]}
                      key={hit.objectID}
                    />
                  ))
                ))
              )}
            </div>
          </div>
          {items.length > 3 && (
            <div className="col-12 col-lg-auto ms-lg-auto mt-2 mt-lg-0">
              <a
                href={`${additionalData.baseUrl}${items[0] && items[0].section}/${results.query && `?query=${results.query}`}`}
                className="more-link-sm"
              >
                {Craft.t('search', 'All results for {section}', { section: key.slice(2) })}
                <ArrowRightSharpRegular />
              </a>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
